import React from 'react';
import { adopt } from 'react-adopt';
import { Mutation, Query } from '@apollo/client/react/components';
import { gql } from '@apollo/client';
import { useUserContext } from '@blocs.auth';
import { ReactAdopt } from '@blocs.common';
import { appConfig } from '@minecraft.app-config';
import { gqlCountryEnum } from '@minecraft.graphql-types';
import { MUTATION_UPDATE_PROFILE } from '../../EditProfileGraphqls';

const { areAllQueryDataFetched } = ReactAdopt;
const { imperialDimensionsCountries } = appConfig;

export const GET_PROFILES = gql`
  query profiles($ids: ID!) {
    profiles(ids: [$ids]) {
      profileId
      height {
        id
        IMPERIAL_FEET_INCHES
        METRIC_CENTIMETERS
      }
      weight {
        id
        IMPERIAL_LBS
        kgRounded
        METRIC_KG
      }
    }
  }
`;

export const GET_HEIGHTS = gql`
  query heights {
    heights {
      id
      IMPERIAL_FEET_INCHES
      METRIC_CENTIMETERS
    }
  }
`;

export const GET_WEIGHTS = gql`
  query weights {
    weights {
      id
      IMPERIAL_LBS
      showInMetricUI
      kgRounded
      METRIC_KG
    }
  }
`;

const generateComposed = (props) =>
  adopt({
    updateProfile: ({ render }) => <Mutation mutation={MUTATION_UPDATE_PROFILE}>{render}</Mutation>,
    profiles: ({ render }) => (
      <Query query={GET_PROFILES} variables={{ ids: props.profileId }}>
        {render}
      </Query>
    ),
    heights: ({ render }) => <Query query={GET_HEIGHTS}>{render}</Query>,
    weights: ({ render }) => <Query query={GET_WEIGHTS}>{render}</Query>,
  });

const Connector = (props) => {
  const Composed = generateComposed(props);
  const { myAccount } = useUserContext();

  return (
    <Composed>
      {({ updateProfile, profiles, heights, weights }) => {
        if (!areAllQueryDataFetched([profiles, heights, weights])) {
          return null;
        }

        const isImperialAccountUnitSystem = imperialDimensionsCountries.includes(
          myAccount?.country?.code as gqlCountryEnum
        );

        const mappedHeights = isImperialAccountUnitSystem
          ? heights.data.heights.map((height) => ({
              label: height.IMPERIAL_FEET_INCHES,
              value: height.IMPERIAL_FEET_INCHES,
              ...height,
            }))
          : heights.data.heights.map((height) => ({
              label: `${height.METRIC_CENTIMETERS} Cm`,
              value: height.METRIC_CENTIMETERS,
              ...height,
            }));

        const mappedWeights = isImperialAccountUnitSystem
          ? weights.data.weights.map((weight) => ({
              label: `${weight.IMPERIAL_LBS} Lbs`,
              value: weight.IMPERIAL_LBS,
              ...weight,
            }))
          : weights.data.weights
              .filter((w) => w.showInMetricUI)
              .map((weight) => ({
                label: `${weight.kgRounded} Kg`,
                value: weight.kgRounded,
                ...weight,
              }));

        const { height: profileHeight = {}, weight: profileWeight = {} } = profiles?.data?.profiles?.[0] || {};
        const profileHeightOption = isImperialAccountUnitSystem
          ? {
              label: profileHeight?.IMPERIAL_FEET_INCHES || '',
              value: profileHeight?.IMPERIAL_FEET_INCHES || '',
              ...profileHeight,
            }
          : {
              label: profileHeight?.METRIC_CENTIMETERS ? `${profileHeight?.METRIC_CENTIMETERS} Cm` : '',
              value: profileHeight?.METRIC_CENTIMETERS || '',
              ...profileHeight,
            };
        const profileWeightOption = isImperialAccountUnitSystem
          ? {
              label: profileWeight?.IMPERIAL_LBS ? `${profileWeight?.IMPERIAL_LBS} Lbs` : '',
              value: profileWeight?.IMPERIAL_LBS || '',
              ...profileWeight,
            }
          : {
              label: profileWeight?.kgRounded ? `${profileWeight?.kgRounded} Kg` : '',
              value: profileWeight?.kgRounded || '',
              ...profileWeight,
            };

        return props.children({
          fetcher: {
            profileHeight: profileHeightOption,
            profileWeight: profileWeightOption,
            allHeights: mappedHeights,
            allWeights: mappedWeights,
            userCountry: myAccount,
            showImperial: imperialDimensionsCountries.includes(myAccount.country.code as gqlCountryEnum),
          },
          updater: {
            updateProfile,
          },
        });
      }}
    </Composed>
  );
};

export default Connector;
